import { renderConfig } from '../constants';
import { LabelPosEnum } from '../generated-api';
import { getCanvasDimensions } from './config';
import { measureText } from './labelPaddings';
import { LabelPaddings, SankeyGraph, SankeyNode } from './types';

// Compute the total space required by the widest label in a stage
function maxLabelWidth(
  formValues: SankeyFormValues,
  pad: LabelPaddings,
  stageArr: SankeyNode[] = [],
  labelsOnLeft: boolean
): number {
  // If we're not showing labels, reserve no space for them.
  if (!formValues.show_labels) {
    return 0;
  }
  const canvas = getCanvasDimensions(formValues);
  const fontSize = formValues.font_size ?? 14;
  let maxWidth = 0;
  stageArr
    .filter((n) => n.name)
    .forEach((n) => {
      let longestText = n.name;
      if (n.labelValue && n.labelValue.length > longestText.length) {
        longestText = n.labelValue;
      }
      if (
        n.labelComparisonLine &&
        n.labelComparisonLine.length > longestText.length
      ) {
        longestText = n.labelComparisonLine;
      }
      const labelW =
        measureText(canvas, fontSize, longestText).width +
        (labelsOnLeft ? pad.lblMarginLeft : pad.lblMarginRight) +
        pad.outer;
      maxWidth = Math.max(maxWidth, labelW);
    });
  return maxWidth;
}

// Compute the final size of the graph
export function setUpDiagramSize(
  formValues: SankeyFormValues,
  pad: LabelPaddings,
  stagesArr: SankeyNode[][]
): SankeyGraph {
  const canvas = getCanvasDimensions(formValues);
  // Calculate the actual room we have to draw in...
  // Start from the user's declared canvas size + paddings:
  const graphW = canvas.width - 2 * renderConfig.canvasPadding.x,
    graphH =
      canvas.height -
      renderConfig.canvasPadding.top -
      renderConfig.canvasPadding.bottom,
    // If any labels are on the LEFT, get stage[0]'s maxLabelWidth:
    leadingW =
      formValues.label_pos === LabelPosEnum.BEFORE ||
      formValues.label_pos === LabelPosEnum.OUTSIDE
        ? maxLabelWidth(formValues, pad, stagesArr[0], true)
        : 0,
    // If any are on the RIGHT, get stage[-1]'s maxLabelWidth:
    trailingW =
      formValues.label_pos === LabelPosEnum.AFTER ||
      formValues.label_pos === LabelPosEnum.OUTSIDE
        ? maxLabelWidth(formValues, pad, stagesArr[stagesArr.length - 1], false)
        : 0,
    // Compute the ideal width to fit everything successfully:
    idealW = graphW - leadingW - trailingW,
    // Find the smallest width we will allow -- all the Node widths
    // plus 5px for every Flow region:
    minimumW =
      stagesArr.length * (formValues.node_width ?? 0) +
      (stagesArr.length - 1) * 5,
    // Pick which width we will actually use:
    finalW = Math.max(idealW, minimumW),
    // Is any part of the diagram going to be cut off?
    // If so, we have to decide how to distribute the bad news.
    //
    // This derives the proportion of any potential cut-off area
    // which shall be attributed to the leading side:
    leadingShareOfError =
      leadingW + trailingW > 0 ? leadingW / (leadingW + trailingW) : 0.5,
    // The actual amount of error (if any) for the leading side:
    leadingCutOffAdjustment =
      idealW < minimumW ? (idealW - minimumW) * leadingShareOfError : 0,
    // Compute the left margin we will actually use:
    finalLeftMargin =
      renderConfig.canvasPadding.x + leadingW + leadingCutOffAdjustment;
  return { w: finalW, h: graphH, leftMargin: finalLeftMargin };
}
